<script setup>
import { computed, ref, watch } from 'vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  companies: {
    type: Array,
    default: () => [],
  },
  modelValue: {
    type: [String, Number],
    default: '',
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close', 'save', 'update:modelValue']);

const query = ref('');
const selectedValue = ref('');

const options = computed(() => [
  { id: '', name: 'No company' },
  ...props.companies,
]);

const filteredOptions = computed(() => {
  const term = query.value.trim().toLowerCase();
  if (!term) return options.value;
  return options.value.filter(option =>
    String(option.name || '').toLowerCase().includes(term)
  );
});

watch(
  () => [props.show, props.modelValue],
  () => {
    if (props.show) {
      selectedValue.value = String(props.modelValue || '');
      query.value = '';
    }
  },
  { immediate: true }
);

const selectOption = option => {
  selectedValue.value = String(option.id || '');
  emit('update:modelValue', selectedValue.value);
};

const save = () => {
  emit('save', selectedValue.value);
};
</script>

<template>
  <div
    v-if="show"
    class="fixed inset-0 z-[1000] flex items-center justify-center bg-n-alpha-black2 px-4 backdrop-blur-[4px]"
    @click.self="$emit('close')"
  >
    <section
      class="flex max-h-[min(34rem,calc(100vh-2rem))] w-full max-w-[400px] flex-col overflow-hidden rounded-xl bg-n-solid-1 shadow-xl"
      role="dialog"
      aria-modal="true"
    >
      <header class="flex shrink-0 items-center gap-3 px-5 py-4">
        <h2 class="m-0 flex-1 text-base font-semibold text-n-slate-12">
          Select company
        </h2>
        <button
          type="button"
          class="flex size-7 items-center justify-center rounded-md text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
          aria-label="Close company selector"
          @click="$emit('close')"
        >
          <span class="i-lucide-x size-4" />
        </button>
      </header>
      <div class="h-px shrink-0 bg-n-weak" />
      <div class="shrink-0 px-5 py-3">
        <label
          class="flex h-10 items-center gap-2 rounded-lg border border-n-weak bg-n-solid-1 px-3 focus-within:border-n-brand focus-within:ring-2 focus-within:ring-n-brand/15"
        >
          <span class="i-lucide-search size-4 text-n-slate-10" />
          <input
            v-model="query"
            type="text"
            class="reset-base h-full min-w-0 flex-1 !border-0 !bg-transparent !p-0 text-sm text-n-slate-12 !outline-0 !outline-none !shadow-none placeholder:text-n-slate-10 [appearance:textfield]"
            placeholder="Search company..."
            aria-label="Search company"
          />
        </label>
      </div>
      <div class="min-h-0 flex-1 overflow-y-auto py-1">
        <button
          v-for="option in filteredOptions"
          :key="option.id || 'none'"
          type="button"
          class="flex min-h-11 w-full items-center gap-3 px-5 py-2 text-left hover:bg-n-alpha-1"
          :class="{ 'bg-n-brand/5': String(option.id || '') === selectedValue }"
          @click="selectOption(option)"
        >
          <span
            class="relative size-5 rounded-full border"
            :class="
              String(option.id || '') === selectedValue
                ? 'border-n-brand'
                : 'border-n-strong'
            "
          >
            <span
              v-if="String(option.id || '') === selectedValue"
              class="absolute left-1/2 top-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-n-brand"
            />
          </span>
          <span class="min-w-0 flex-1 truncate text-sm text-n-slate-12">
            {{ option.name }}
          </span>
        </button>
        <p
          v-if="!filteredOptions.length"
          class="m-0 px-5 py-6 text-center text-sm text-n-slate-10"
        >
          No companies found
        </p>
      </div>
      <p v-if="error" class="m-0 px-5 pb-2 text-xs text-n-ruby-10">
        {{ error }}
      </p>
      <div class="h-px shrink-0 bg-n-weak" />
      <footer class="flex shrink-0 justify-end gap-2 bg-n-solid-1 px-5 py-4">
        <NextButton faded slate label="Cancel" @click="$emit('close')" />
        <NextButton
          blue
          label="Save"
          :is-loading="isLoading"
          @click="save"
        />
      </footer>
    </section>
  </div>
</template>
